// ============================================================================
// Avec Sync — Motor de sincronização
// ============================================================================
// Orquestra todas as chamadas à API Avec para cada unidade e profissional.
//
// Regras (definidas pelo usuário):
// - Cada unidade sincronizada SEPARADAMENTE
// - Cada profissional sincronizado SEPARADAMENTE
// - Rede = soma das unidades
// - Polling a cada 60 segundos (configurável)
// - Ao fechar mês → snapshot permanente
// ============================================================================

import { fetchReport, fetchAllPages, formatAvecDate, getMonthRange, agoraAvec } from '../utils/avecApi.js';
import { isBeverageProduct } from '../utils/beverageFilter.js';
import { AVEC_COLETA_ESTADO, AVEC_GRADE_DIARIA_GUARDA, ATENDIDOS_SO_COMANDA_SERVICO, DASH_SEM_DADO } from '../config/featureFlags.js';
import { escolherAtendidos } from '../utils/atendidosRegua.js';
import { AVEC_CONFIG } from '../config/avecConfig.js';
import {
  decidirValorDia, AUSENTE, CHAVE_VAZIOS_GRADE, lerVaziosGrade, anotarVazioGrade, gravarVaziosGrade,
} from '../utils/gradeDiaria.js';
import { PRIORIDADE } from '../utils/avecRetentativa.js';
import {
  VERSAO_COLETA, respostaVazia, mesTemFalhas, expandirGruposRetentativa, protegerCamposComFalha, cortadaPeloPrazo,
} from '../utils/avecFalhas.js';
import { CHAVE_RESSYNC, lerMarcasRessync, mesesParaConsolidarComFalha } from '../utils/avecRessync.js';
import { AVEC_UNITS, getConfiguredUnits } from '../data/avecUnitsMap.js';
import {
  transformFaturamento,
  transformServicosRealizados,
  transformClientesAtendidos,
  transformComandas,
  transformClientesNovos,
  transformProdutosVendidos,
  transformAgendamentosPreferenciais,
  transformTaxaRetorno,
  transformPacotesVendidos,
  transformServicosExtras,
  transformFaturamentoDiario,
  transformFaturamentoProfissional,
  transformServicosPorProfissional,
  transformProdutosPorProfissional,
  transformComissoesProfissional,
  transformPacotesPorProfissional,
  aggregateNetworkFromUnits,
} from '../utils/avecTransformers.js';

const CACHE_KEY = 'fk_avec_cache';
const SNAPSHOTS_KEY = 'fk_avec_snapshots';
const DAILY_CACHE_KEY = 'fk_avec_daily_cache';
// Estrutura DAILY: { 'YYYY-MM-DD': { unitKey: { faturamento: 123.45 } } }

// ─── Cache local (localStorage) ─────────────────────────────────────────────
export function getCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); }
  catch { return {}; }
}
export function setCache(data) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(data));
}

export function getSnapshots() {
  try { return JSON.parse(localStorage.getItem(SNAPSHOTS_KEY) || '{}'); }
  catch { return {}; }
}

// ─── Cache diário (Meta x Realizado) ──────────────────────────────────────
export function getDailyCache() {
  try { return JSON.parse(localStorage.getItem(DAILY_CACHE_KEY) || '{}'); }
  catch { return {}; }
}
export function setDailyCache(data) {
  localStorage.setItem(DAILY_CACHE_KEY, JSON.stringify(data));
}

/**
 * Busca o faturamento (0281) de UM dia específico, para UMA unidade.
 * Usado para montar a grade diária do Meta x Realizado.
 */
export async function fetchDailyFaturamento(unitKey, dateStr /* 'YYYY-MM-DD' */) {
  const unit = AVEC_UNITS[unitKey];
  if (!unit?.avecSalaoId) throw new Error(`Unidade ${unitKey} sem avecSalaoId`);
  const [y, m, d] = dateStr.split('-').map(Number);
  const br = formatAvecDate(new Date(y, m - 1, d));
  // Caminho de antes da A9 (AVEC_GRADE_DIARIA_GUARDA desligada): SEM prioridade
  // de fundo (revisão 23/10/2026, achado 9). Aqui a falha sem valor anterior
  // grava 0, então a reversão da A9 precisa das novas tentativas de antes (o
  // laço antigo repetia 429/503/timeout até 2 vezes); com a A2 ligada a
  // prioridade padrão (1) dá as mesmas 3 tentativas.
  const resp = await fetchReport('0281', {
    salao: unit.avecSalaoId,
    inicio: br,
    fim: br,
  });
  return transformFaturamento(resp).faturamento;
}

/**
 * A9 (23/10/2026): o mesmo 0281 de um dia, com o ESTADO: { estado: 'ok' | 'vazio', valor }.
 * Falha (rede, 403, corpo inválido...) rejeita. Prioridade de fundo (1 tentativa:
 * a próxima rodada de 60 s é a nova tentativa e a grade segura o valor anterior).
 */
export async function fetchDailyFaturamentoEstado(unitKey, dateStr /* 'YYYY-MM-DD' */) {
  const unit = AVEC_UNITS[unitKey];
  if (!unit?.avecSalaoId) throw new Error(`Unidade ${unitKey} sem avecSalaoId`);
  const [y, m, d] = dateStr.split('-').map(Number);
  const br = formatAvecDate(new Date(y, m - 1, d));
  const resp = await fetchReport('0281', { salao: unit.avecSalaoId, inicio: br, fim: br }, { prioridade: PRIORIDADE.FUNDO });
  const valor = transformFaturamento(resp).faturamento;
  return { estado: (respostaVazia(resp) || !(valor > 0)) ? 'vazio' : 'ok', valor };
}
const avisosGrade = new Set();

/**
 * Sincroniza o faturamento diário de TODAS as unidades para um mês.
 * Usa cache por dia (não re-puxa dias já cacheados, exceto o dia de hoje).
 * @returns {Object} { 'YYYY-MM-DD': { unitKey: faturamento, _total: sum } }
 */
export async function syncDailyFaturamentoMonth(year, month, opts = {}) {
  const { force = false, onProgress } = opts;
  const units = getConfiguredUnits();
  const cache = getDailyCache();
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  // Mês corrente: TODOS os dias re-fetcham (capta ajustes retroativos
  // de comandas, edições manuais na Avec, etc).
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const daysInMonth = new Date(year, month, 0).getDate();
  const dates = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    if (ds <= todayStr) dates.push(ds);
  }

  let processed = 0;
  const total = dates.length * units.length;
  // A9 (revisão 23/10/2026, achado 14): 200 vazio por cima de valor bom só segura
  // o anterior por um tempo; vazio que se repete vira o zero real do Avec (venda
  // cancelada). Marca só do aparelho (gradeDiaria.js: CHAVE_VAZIOS_GRADE).
  const agoraMs = agoraAvec();
  let vazios = {};
  if (AVEC_GRADE_DIARIA_GUARDA) { try { vazios = lerVaziosGrade(localStorage.getItem(CHAVE_VAZIOS_GRADE)); } catch { vazios = {}; } }

  for (const dateStr of dates) {
    if (!cache[dateStr]) cache[dateStr] = {};
    const tasks = units.map(async (u) => {
      // Skip apenas em meses PASSADOS já cacheados (imutáveis).
      // Mês corrente: sempre re-fetch — pega ajustes retroativos.
      if (!force && !isCurrentMonth && cache[dateStr][u.key] !== undefined) return;
      if (AVEC_GRADE_DIARIA_GUARDA) {
        // A9: falha não vira 0; 200 vazio não apaga valor bom (gradeDiaria.js)
        const anterior = cache[dateStr][u.key];
        let res;
        try { res = await fetchDailyFaturamentoEstado(u.key, dateStr); }
        catch { res = { estado: 'falhou' }; }
        const chaveVazio = `${dateStr}|${u.key}`;
        vazios = anotarVazioGrade(vazios, chaveVazio, res.estado, agoraMs);
        const v = decidirValorDia(anterior, res, { vazio: vazios[chaveVazio], ehHoje: dateStr === todayStr, agora: agoraMs });
        if (v === AUSENTE) delete cache[dateStr][u.key];
        else cache[dateStr][u.key] = v;
        if (res.estado === 'vazio' && Number(anterior) > 0) {
          if (v === 0) {
            console.info(`[AvecSync daily] ${u.key} ${dateStr}: Avec respondeu vazio ${vazios[chaveVazio]?.n || 0} vezes seguidas; aceito o zero (antes ${anterior})`);
            vazios = anotarVazioGrade(vazios, chaveVazio, 'aceito', agoraMs);
          } else if (!avisosGrade.has(chaveVazio)) {
            avisosGrade.add(chaveVazio);
            console.info(`[AvecSync daily] ${u.key} ${dateStr}: Avec respondeu vazio, mantido o valor anterior (${anterior})`);
          }
        }
        processed++;
        if (onProgress) onProgress(processed, total, dateStr, u.key);
        return;
      }
      try {
        const valor = await fetchDailyFaturamento(u.key, dateStr);
        cache[dateStr][u.key] = valor;
      } catch (err) {
        // Não sobrescreve com 0 se já tinha valor (preserva fallback)
        if (cache[dateStr][u.key] === undefined) cache[dateStr][u.key] = 0;
      } finally {
        processed++;
        if (onProgress) onProgress(processed, total, dateStr, u.key);
      }
    });
    await Promise.allSettled(tasks);
    setDailyCache(cache);
  }
  if (AVEC_GRADE_DIARIA_GUARDA) { try { localStorage.setItem(CHAVE_VAZIOS_GRADE, gravarVaziosGrade(vazios, agoraMs)); } catch { /* só memória do aparelho */ } }
  return cache;
}

/**
 * Retorna o faturamento diário de uma unidade (ou rede) para um mês, formato:
 *   { 'YYYY-MM-DD': valor }
 */
export function getDailyFaturamentoForMonth(year, month, unitKey = null) {
  const cache = getDailyCache();
  const prefix = `${year}-${String(month).padStart(2, '0')}-`;
  const result = {};
  for (const [date, byUnit] of Object.entries(cache)) {
    if (!date.startsWith(prefix)) continue;
    if (unitKey) {
      result[date] = byUnit[unitKey] || 0;
    } else {
      // Rede: soma todas unidades
      result[date] = Object.values(byUnit).reduce((s, v) => s + (v || 0), 0);
    }
  }
  return result;
}
export function setSnapshot(year, month, unitKey, data) {
  const all = getSnapshots();
  const key = `${year}-${String(month).padStart(2, '0')}`;
  if (!all[key]) all[key] = {};
  all[key][unitKey] = { ...data, _snapshotAt: new Date().toISOString() };
  localStorage.setItem(SNAPSHOTS_KEY, JSON.stringify(all));
}

// ─── Helpers de data ──────────────────────────────────────────────────────

/**
 * Calcula os períodos para taxa de retorno conforme regra Feel King:
 * - Período 1 (P1): últimos 60 dias antes do período atual (30 dias atrás)
 * - Período 2 (P2): últimos 30 dias
 *
 * Ex: se hoje é 22/04/2026 → P1=01/03 a 31/03, P2=01/04 a 30/04
 */
function getTaxaRetornoPeriodos(year, month) {
  // month: 1-12
  // P2 = mês corrente (ou do snapshot)
  const inicio2 = formatAvecDate(new Date(year, month - 1, 1));
  const fim2 = formatAvecDate(new Date(year, month, 0));
  // P1 = mês anterior
  const inicio1 = formatAvecDate(new Date(year, month - 2, 1));
  const fim1 = formatAvecDate(new Date(year, month - 1, 0));
  return { inicio1, fim1, inicio2, fim2 };
}

// ─── Sync por unidade ───────────────────────────────────────────────────────

// Teto de páginas do 0031 paginado (0031p). Lista com esse tamanho todo pode ter
// sido cortada: a contagem de comandas dela não vale (atendidosRegua.js).
const MAX_PAGINAS_0031P = 20;

/**
 * Converte 'YYYY-MM-DD' para Date local (sem timezone shift).
 */
function parseISODate(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Sincroniza TODOS os KPIs de uma unidade para um RANGE de datas arbitrário.
 * @param {string} unitKey - chave em AVEC_UNITS (ex: 'moema')
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {string} endDate - 'YYYY-MM-DD'
 * @param {object} opts - { skipTaxaRetorno: bool, skipExtrasPorProf: bool }
 * @returns {Promise<object>} - KPIs transformados da unidade
 */
// 23/10/2026 (A1/A2): opts aditivos, todos opcionais:
//   coletaEstado (padrão AVEC_COLETA_ESTADO): _coleta/_falhas/_visitasFonte e
//     campo que falhou = null (nunca 0). false = exatamente como antes;
//   prioridade / deadline: repassados a cada pedido ao Avec (avecApi);
//   semPacotesPorProf: pula o leque do 0065 e o 0225 (os Dashboards não usam);
//   reaproveitar + somente: nova tentativa só do que faltou (retentarFalhasRange):
//     código fora de `somente` reaproveita o resultado cru da coleta anterior.
export async function syncUnitRange(unitKey, startDate, endDate, opts = {}) {
  const unit = AVEC_UNITS[unitKey];
  if (!unit || !unit.avecSalaoId) {
    throw new Error(`Unidade "${unitKey}" não tem avecSalaoId configurado`);
  }
  const salao = unit.avecSalaoId;
  const inicio = formatAvecDate(parseISODate(startDate));
  const fim = formatAvecDate(parseISODate(endDate));
  const common = { salao, inicio, fim };
  const CE = opts.coletaEstado ?? AVEC_COLETA_ESTADO;
  const t0Coleta = agoraAvec();
  const req = {};
  if (opts.prioridade != null) req.prioridade = opts.prioridade;
  if (opts.deadline != null) req.deadline = opts.deadline;
  const semPac = !!opts.semPacotesPorProf;
  const reuso = CE && opts.reaproveitar ? opts.reaproveitar : null;
  const somente = reuso && Array.isArray(opts.somente) ? new Set(opts.somente) : null;
  const reusa = (code) => !!(reuso && somente && !somente.has(code) && reuso.settled?.[code]);
  const deSettled = (st) => (st.status === 'fulfilled' ? Promise.resolve(st.value) : Promise.reject(st.reason));
  const obter = (code, fabrica) => (reusa(code) ? deSettled(reuso.settled[code]) : fabrica());
  const assentar = (p) => p.then(value => ({ status: 'fulfilled', value }), reason => ({ status: 'rejected', reason }));

  // ── Fim "clamped" ao dia de hoje pra reports que contam AGENDA ──
  // O relatório 0326 (agendamentos preferenciais) inclui agendamentos FUTUROS
  // já marcados na agenda. Se chamarmos com fim = último dia do mês, o número
  // contém pessoas que ainda nem chegaram pra atender — pode aparecer prof
  // com 8 "preferenciais" mesmo só tendo atendido 2 hoje.
  // Solução: pra mês corrente, limita o "fim" do 0326 a hoje. Pra meses
  // passados, mantém igual (todos os agendamentos do mês já foram realizados).
  const today = new Date();
  const endParsed = parseISODate(endDate);
  const isFutureEnd = endParsed > today;
  const fimAgenda = isFutureEnd ? formatAvecDate(today) : fim;
  const commonAgenda = { salao, inicio, fim: fimAgenda };

  // Chamadas em paralelo
  // NOTA: 0121 e 0065 removidos — exigem profissional_id obrigatório.
  // Substituímos por: 0225 (ranking pacotes por prof) e agregação local do 0041 (produtos).
  // Tabela [código, pedido] na MESMA ordem de sempre (o código nomeia cada resultado).
  const TABELA = [
    ['0281', () => fetchReport('0281', common, req)],
    ['0031', () => fetchReport('0031', common, req)],
    ['0002', () => fetchReport('0002', { ...common, como_conheceu: '' }, req)],
    ['0012', () => fetchReport('0012', common, req)],
    ['0041', () => fetchReport('0041', common, req)],
    ['0326', () => fetchReport('0326', commonAgenda, req)], // ← clamped: só agenda já realizada
    ['0062', () => fetchReport('0062', common, req)],
    ['0032', () => fetchReport('0032', common, req)],
    ['0088', () => fetchReport('0088', common, req)],
    ['0021', () => fetchReport('0021', { ...common, tipo: 'todos' }, req)],
    ['0123', () => fetchReport('0123', common, req)],
    // 0225: os Dashboards não leem (semPacotesPorProf = pedido enxuto)
    ['0225', () => (semPac ? Promise.resolve({ result: [] }) : fetchReport('0225', common, req))],
    ['0089', () => fetchReport('0089', common, req)],
    // 0064 paginado — cada comanda aqui é "consumo de pacote/assinatura":
    // cliente já pagou antes, então NÃO gera faturamento na comanda atual.
    // Usado pra ajustar o ticket médio excluindo essas comandas do divisor.
    ['0064', () => fetchAllPages('0064', common, { maxPages: 30, ...req })],
    // 0031 item-a-item COMPLETO (paginado) — só quando o caller pede
    // (duelos semanais). O 0031 single-page acima trunca em 250 linhas, o
    // que basta pros totais da unidade mas NÃO pra agregar R$ por prof.
    // Fora do opt-in resolve vazio: custo zero pro auto-sync de 60s.
    ['0031p', () => (opts.withExtras0031
      ? fetchAllPages('0031', common, { maxPages: MAX_PAGINAS_0031P, ...req }).then(rows => ({ result: rows }))
      : Promise.resolve({ result: [] }))],
    // 0033 (tabela de preços): valora consumo de pacote a tabela × 0,8 —
    // regra do dono 12/08/2026. Só com o 0031 (mesmos consumidores).
    ['0033', () => (opts.withExtras0031
      ? fetchReport('0033', { salao, page: 1, limit: 250 }, req)
      : Promise.resolve({ result: [] }))],
    // 0063: pacotes do cliente com valor_pacote/total — dá o RATEIO REAL da
    // sessão consumida (igual ao app da Avec). Caso Kayque 22/08/2026.
    ['0063', () => (opts.withExtras0031
      ? fetchAllPages('0063', common, { maxPages: 20, ...req }).then(rows => ({ result: rows }))
      : Promise.resolve({ result: [] }))],
  ];
  const [
    faturamento,
    servicos,
    clientes,
    novos,
    produtos,
    agendamentos,
    pacotes,
    extras,
    faturamentoDiario,
    profissionaisFat,
    profissionaisComissao,
    profissionaisPacotes, // 0225
    comandas,             // 0089 — fonte REAL de "visitas"
    itensPacote,          // 0064 — itens vendidos como parte de pacote/assinatura
    servicos0031Full,     // 0031 PAGINADO — extras por prof p/ DUELOS (opt-in)
    tabelaPrecos0033,     // tabela de preços — valora consumo de pacote (opt-in)
    pacotesCliente0063,   // pacotes do cliente — rateio real (valor_pacote/total) (opt-in)
  ] = await Promise.allSettled(TABELA.map(([code, fabrica]) => obter(code, fabrica)));
  // Resultado cru por código (nova tentativa só do que faltou)
  const S = {};
  TABELA.forEach(([code], k) => { S[code] = [faturamento, servicos, clientes, novos, produtos, agendamentos, pacotes, extras,
    faturamentoDiario, profissionaisFat, profissionaisComissao, profissionaisPacotes, comandas, itensPacote,
    servicos0031Full, tabelaPrecos0033, pacotesCliente0063][k]; });

  // Taxa de retorno precisa de 2 períodos. Só calcula em modo "mês" (opts.monthMeta).
  let retorno = { taxaRetorno: 0 };
  const pede0007 = !!(opts.monthMeta && !opts.skipTaxaRetorno);
  if (pede0007) {
    const { _year, _month } = opts.monthMeta;
    const retornoPeriodos = getTaxaRetornoPeriodos(_year, _month);
    S['0007'] = await assentar(obter('0007', () => fetchReport('0007', { salao, ...retornoPeriodos }, req)));
    retorno = S['0007'].status === 'fulfilled' ? transformTaxaRetorno(S['0007'].value) : { taxaRetorno: 0 };
  }

  // Extrair valores (ou fallback se falhou)
  const unwrap = (promise, transformer, fallback) =>
    promise.status === 'fulfilled' ? transformer(promise.value) : fallback;

  const uFat = unwrap(faturamento, transformFaturamento, { faturamento: 0, formasPagamento: [] });
  const uSvc = unwrap(servicos, transformServicosRealizados, { servicosRealizados: 0, porServico: [] });
  const uCli = unwrap(clientes, transformClientesAtendidos, { visitas: 0, clientesUnicos: 0 });
  const uComandas = unwrap(comandas, transformComandas, { totalAtendimentos: 0, comandasServico: 0, comandasProdutoServico: 0, comandasProduto: 0 });
  // Override: "visitas" agora é o TOTAL DE ATENDIMENTOS (SERVICO + PRODUTO/SERVICO) do 0089
  // Isso bate com o que a Avec exibe no painel web.
  if (uComandas.totalAtendimentos > 0) uCli.visitas = uComandas.totalAtendimentos;
  const uNovos = unwrap(novos, transformClientesNovos, { clientesNovos: 0 });
  const uProd = unwrap(produtos, transformProdutosVendidos, { produtosVendidos: 0, faturamentoProdutos: 0 });
  const uAgend = unwrap(agendamentos, transformAgendamentosPreferenciais, { agendamentosPreferenciais: 0, semPreferencia: 0 });
  const uPac = unwrap(pacotes, transformPacotesVendidos, { pacotesVendidosTotal: 0, porPacote: [] });
  const uExt = unwrap(extras, transformServicosExtras, { extras: [] });
  const uDia = unwrap(faturamentoDiario, transformFaturamentoDiario, { porDia: {} });
  const pFat = unwrap(profissionaisFat, transformFaturamentoProfissional, []);
  const pCom = unwrap(profissionaisComissao, transformComissoesProfissional, []);
  const pPac = unwrap(profissionaisPacotes, transformPacotesPorProfissional, []);
  // Produtos por profissional: agregado local a partir do 0041 (já temos)
  const pProd = (() => {
    if (produtos.status !== 'fulfilled') return [];
    const items = produtos.value.result || [];
    const byProf = {};
    for (const it of items) {
      const nome = it.profissional || '';
      if (!nome) continue;
      // Bebida não é venda de produto do prof — MESMA régua do daily-sync
      // (12/08/2026): sem isso o "você fez hoje" da meta de performance
      // somava a cerveja que o acumulado oficial nunca teria.
      if (isBeverageProduct(it.categoria, it.nome)) continue;
      if (!byProf[nome]) byProf[nome] = { nome, qtdProdutos: 0, faturamentoProdutos: 0 };
      byProf[nome].qtdProdutos += 1; // cada linha do 0041 é uma venda
      byProf[nome].faturamentoProdutos += Number(it.valor_total || it.valor || it.total || 0);
    }
    return Object.values(byProf);
  })();

  // Extras por prof via 0031 (p/ DUELOS de performance — 12/08/2026).
  // Linha do 0031: { item (serviço), profissional, valor_total }. valor 0 =
  // consumo de pacote (cliente já pagou antes) → fora, igual ao daily-sync.
  const extras0031PorProf = (() => {
    if (servicos0031Full.status !== 'fulfilled') return {};
    const rows = servicos0031Full.value?.result || [];
    const byProf = {};
    for (const it of rows) {
      const v = Number(it.valor_total ?? it.valor ?? 0);
      if (!(v > 0)) continue;
      const nome = String(it.profissional || '').trim();
      if (!nome) continue;
      if (!byProf[nome]) byProf[nome] = [];
      byProf[nome].push({ servico: it.item, faturamento: v });
    }
    return byProf;
  })();

  // Linhas de CONSUMO DE PACOTE (valor 0) por prof — o executor ganha
  // tabela × 0,8 na performance. Classificação extra-ou-não fica pro
  // consumidor (mesma régua curada do catálogo).
  const extrasPacote0031PorProf = (() => {
    if (servicos0031Full.status !== 'fulfilled') return {};
    const rows = servicos0031Full.value?.result || [];
    // RATEIO REAL da sessão: 0063 (cliente|pacote → valor_pacote/total) ×
    // 0064 (comanda+item → qual pacote de quem). Igual ao app da Avec —
    // tabela×0,8 fica como fallback no consumidor (avecExtrasPacoteToValor).
    const normJ = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
    const pkgVal = {};
    if (pacotesCliente0063.status === 'fulfilled') {
      for (const it of (pacotesCliente0063.value?.result || [])) {
        const tot = Number(it.total) || 0;
        const vp = Number(it.valor_pacote) || 0;
        if (tot > 0 && vp > 0) pkgVal[`${normJ(it.nome)}|${normJ(it.pacote)}`] = vp / tot;
      }
    }
    // Chave COM DATA: `numero` é sequência DIÁRIA de comanda e colide entre
    // dias no range semanal dos duelos (review 22/08/2026). Ordem do valor:
    // 0063 (nominal, igual ao app) → 0064.valor (sobrevive a pacote
    // consumido/expirado — o 0063 é foto ao vivo) → tabela×0,8 no helper.
    const sessaoMap = {};
    if (itensPacote.status === 'fulfilled') {
      for (const it of (itensPacote.value || [])) {
        const sessao = pkgVal[`${normJ(it.nome)}|${normJ(it.pacote)}`];
        const valor64 = Number(it.valor) || 0;
        const credito = sessao > 0 ? sessao : valor64;
        if (credito > 0) sessaoMap[`${it.data}|${it.numero}|${normJ(it.item)}`] = credito;
      }
    }
    const byProf = {};
    for (const it of rows) {
      const v = Number(it.valor_total ?? it.valor ?? 0);
      if (v > 0) continue;
      const nome = String(it.profissional || '').trim();
      if (!nome) continue;
      if (!byProf[nome]) byProf[nome] = [];
      byProf[nome].push({
        servico: it.item,
        valorSessao: sessaoMap[`${it.data}|${it.numero}|${normJ(it.item)}`] || 0,
      });
    }
    return byProf;
  })();

  // Tabela { nome do serviço: valor } (cru — consumidor normaliza)
  const tabelaPrecos = (() => {
    if (tabelaPrecos0033.status !== 'fulfilled') return {};
    const map = {};
    for (const it of (tabelaPrecos0033.value?.result || [])) {
      if (it.servico) map[it.servico] = Number(it.valor) || 0;
    }
    return map;
  })();

  // Lista COMPLETA de profissionais Avec (0229) — usada pro matching inteligente
  S['0229'] = await assentar(obter('0229', () => fetchReport('0229', { salao, page: 1, limit: 250 }, req)));
  const profListResp = S['0229'].status === 'fulfilled' ? S['0229'].value : null;
  const avecProfessionals = profListResp?.result || [];

  // Comandas de PACOTE/ASSINATURA por prof — extrai do 0064
  // Key: apelido (campo `apelido` do item). Conta comandas únicas (numero+data).
  // Usado pra ajustar ticket médio: cliente de assinatura entra como atendido
  // mas não conta no divisor do ticket (pq não gerou faturamento na comanda).
  const comandasPacotePorProf = (() => {
    if (itensPacote.status !== 'fulfilled') return {};
    // fetchAllPages retorna ARRAY direto (não {result}) — `.value?.result`
    // deixava isso SEMPRE vazio desde 25/05 (achado da review 22/08/2026)
    const items = itensPacote.value || [];
    const byProf = {}; // { apelido: Set("numero|data") }
    for (const it of items) {
      const apelido = (it.apelido || '').trim();
      if (!apelido) continue;
      const key = `${it.numero}|${it.data}`;
      if (!byProf[apelido]) byProf[apelido] = new Set();
      byProf[apelido].add(key);
    }
    // Converte Set → count
    const out = {};
    for (const [apelido, set] of Object.entries(byProf)) out[apelido] = set.size;
    return out;
  })();

  // Para cada profissional com faturamento, busca 0024 (serviços do prof) em paralelo
  const extrasPorProf = {};
  // Alguma chamada 0024 falhou (401/rede)? O shape zerado é contrato dos
  // consumidores — o flag dá identidade à falha sem mudar o formato.
  let extrasPorProfError = false;
  // Pacotes vendidos POR PROFISSIONAL via relatório 0065 (precisão real de pacotes,
  // permite identificar a recepcionista que vendeu cada pacote).
  // Format: { [nomeProfAvec]: [{ nome: pacoteName, qtd, valor }, ...] }
  const pacotesPorProf = {};

  const normalizeN = s => String(s || '').toLowerCase().trim();
  const getAvecId = (nome) => {
    const n = normalizeN(nome);
    const found = avecProfessionals.find(p =>
      normalizeN(p.nome) === n || normalizeN(p.apelido) === n
    );
    return found?.id || null;
  };

  const falhas0024 = [];
  if (!opts.skipExtrasPorProf && pFat.length > 0 && avecProfessionals.length > 0) {
    const extraTasks = pFat
      .map(p => ({ p, avecId: getAvecId(p.nome) }))
      .filter(x => x.avecId)
      .map(async ({ p, avecId }) => {
        try {
          const resp = await fetchReport('0024', { salao, inicio, fim, profissional_id: avecId }, req);
          extrasPorProf[p.nome] = transformServicosPorProfissional(resp);
        } catch (err) {
          extrasPorProf[p.nome] = [];
          extrasPorProfError = true;
          falhas0024.push(avecId);
        }
      });
    await Promise.allSettled(extraTasks);
  }

  // Pacotes via 0065 — chamada pra TODOS os profissionais ativos da unidade
  // (não apenas os do pFat, porque recepcionistas geralmente não têm faturamento
  // próprio no 0021 mas vendem pacotes). Filtra cargos que vendem pacotes.
  // semPacotesPorProf (pedido enxuto dos Dashboards): leque inteiro pulado.
  const r0065 = {};
  const falhas0065 = [];
  if (avecProfessionals.length > 0 && !semPac) {
    const sellsPackages = (cargo) => {
      const c = normalizeN(cargo || '');
      return c.includes('recep') || c.includes('admin') || c.includes('gerent') || c.includes('caixa') || c.includes('vended');
    };
    // Lista de profs que potencialmente vendem pacotes:
    // - todos com cargo de recepcionista/admin/gerente/etc
    // - + os que aparecem no 0021 (pra cobrir qualquer prof que vendeu)
    const pacoteCandidates = new Map();
    for (const a of avecProfessionals) {
      if (sellsPackages(a.cargo)) pacoteCandidates.set(a.id, { id: a.id, nome: a.nome });
    }
    for (const p of pFat) {
      const id = getAvecId(p.nome);
      if (id && !pacoteCandidates.has(id)) pacoteCandidates.set(id, { id, nome: p.nome });
    }
    // Nova tentativa só do que faltou: com o 0229 reaproveitado, o 0065 dos
    // candidatos que deram certo também é reaproveitado.
    const reuso0065 = reuso && reusa('0229') && reuso.r0065 ? reuso.r0065 : null;
    const refaz0065 = !reuso0065 || (somente && somente.has('0065'));
    const pkgTasks = [...pacoteCandidates.values()].map(async ({ id, nome }) => {
      const antigo = reuso0065?.[id];
      if (antigo && (antigo.ok || !refaz0065)) {
        r0065[id] = antigo;
        if (antigo.ok && antigo.porPacote) pacotesPorProf[nome] = antigo.porPacote;
        if (!antigo.ok) falhas0065.push(id);
        return;
      }
      try {
        const resp = await fetchReport('0065', { salao, inicio, fim, profissional_id: id }, req);
        const t = transformPacotesVendidos(resp);
        r0065[id] = { ok: true, nome, porPacote: t.pacotesVendidosTotal > 0 ? t.porPacote : null };
        if (t.pacotesVendidosTotal > 0) {
          pacotesPorProf[nome] = t.porPacote; // [{ nome, qtd, valor }, ...]
        }
      } catch (err) {
        // silenciosamente ignora — algum prof pode não ter pacotes
        // (23/10/2026: com AVEC_COLETA_ESTADO o id entra em _coleta.falhou['0065'].ids)
        r0065[id] = { ok: false, nome };
        falhas0065.push(id);
      }
    });
    await Promise.allSettled(pkgTasks);
  }

  // Estrutura consolidada da unidade
  const result = {
    // Unit KPIs
    faturamento: uFat.faturamento,
    formasPagamento: uFat.formasPagamento,
    servicosRealizados: uSvc.servicosRealizados,
    porServico: uSvc.porServico,
    visitas: uCli.visitas,
    clientesUnicos: uCli.clientesUnicos,
    comandasServico: uComandas.comandasServico,
    comandasProdutoServico: uComandas.comandasProdutoServico,
    comandasProduto: uComandas.comandasProduto,
    clientesNovos: uNovos.clientesNovos,
    produtosVendidos: uProd.produtosVendidos,
    faturamentoProdutos: uProd.faturamentoProdutos,
    agendamentosPreferenciais: uAgend.agendamentosPreferenciais,
    semPreferencia: uAgend.semPreferencia,
    // Geração de demanda da UNIDADE = clientes novos + sem preferência
    // (definição do dono, 04/08/2026).
    geracaoDemanda: uAgend.semPreferencia + uNovos.clientesNovos,
    taxaRetorno: retorno.taxaRetorno,
    pacotesVendidosTotal: uPac.pacotesVendidosTotal,
    valorPacotes: uPac.valorTotalPacotes || 0,  // R$ faturado em pacotes (0062)
    porPacote: uPac.porPacote,
    extras: uExt.extras,
    faturamentoDiario: uDia.porDia,
    // Derivados
    // Total de comandas de pacote/assinatura na unidade (soma de todos os profs).
    // Subtraímos do divisor do ticket médio — assinatura não gera fat na comanda
    // e não deve diluir o ticket.
    _comandasPacoteTotal: Object.values(comandasPacotePorProf).reduce((s, v) => s + v, 0),
    ticketMedio: (() => {
      const pacotes = Object.values(comandasPacotePorProf).reduce((s, v) => s + v, 0);
      const divisor = Math.max(uCli.visitas - pacotes, 0);
      return divisor > 0 ? uFat.faturamento / divisor : 0;
    })(),
    ticketMedioBruto: uCli.visitas > 0 ? uFat.faturamento / uCli.visitas : 0,
    tracaoVendas: uCli.visitas > 0 ? uSvc.servicosRealizados / uCli.visitas : 0,
    // Profissionais
    profissionais: {
      faturamento: pFat,
      produtos: pProd,
      comissoes: pCom,
      pacotes: pPac,
      agendamentosPorProfissional: uAgend.porProfissional || [],
      extrasPorProfissional: extrasPorProf, // { 'Nome do prof': [{servico, qtd, ...}] }
      extras0031PorProfissional: extras0031PorProf, // { nome: [{servico, faturamento}] } — duelos
      extrasPacote0031PorProfissional: extrasPacote0031PorProf, // { nome: [{servico}] } — consumo de pacote
      tabelaPrecos0033: tabelaPrecos, // { servico: valor } — valora o consumo
      pacotesPorProfissional: pacotesPorProf, // { 'Nome do prof': [{nome, qtd, valor}] } — do 0065
      comandasPacotePorProf: comandasPacotePorProf, // { apelido: qtd comandas de pacote } — do 0064
      avecListCompleta: avecProfessionals,  // [{id, nome, apelido, cpf, cargo, datacad, grupo_acesso}]
    },
    _schemaVersion: 2, // bump quando adicionar novos campos pra forçar re-sync de snapshots antigos
    // Flags de falha (SÓ aditivo — o shape zerado dos campos acima é contrato
    // de dashboards/folha/duelos): distinguem "sem venda" de "sem dados".
    // 0031 paginado falhou → extras0031PorProfissional/extrasPacote0031PorProfissional
    // viraram {} por falha, não por ausência real de extras.
    _extras0031Error: !!(opts.withExtras0031 && servicos0031Full.status !== 'fulfilled'),
    _extrasPorProfError: extrasPorProfError,
    // Metadata
    _unitKey: unitKey,
    _salaoId: salao,
    _startDate: startDate,
    _endDate: endDate,
    ...(opts.monthMeta || {}),
    _fetchedAt: new Date().toISOString(),
  };
  if (!CE) return result;

  // ── Estado de cada relatório (A1, 23/10/2026) ─────────────────────────────
  const pedidos = TABELA.map(([c]) => c).filter(c => {
    if (c === '0031p' || c === '0033' || c === '0063') return !!opts.withExtras0031;
    if (c === '0225') return !semPac;
    return true;
  });
  pedidos.push('0229');
  if (!semPac) pedidos.push('0065');
  const pede0024 = !opts.skipExtrasPorProf;
  if (pede0024) pedidos.push('0024');
  if (pede0007) pedidos.push('0007');
  const falhou = {};
  let vazio = [];
  for (const c of pedidos) {
    const st = S[c];
    if (!st) continue;
    if (st.status === 'rejected') falhou[c] = { motivo: st.reason?.motivo || 'erro', tentativas: st.reason?.tentativas || 1 };
    else if (respostaVazia(st.value)) vazio.push(c);
  }
  const falhou0229 = S['0229']?.status === 'rejected';
  const ordenarIds = (ids) => [...new Set(ids)].sort((a, b) => String(a).localeCompare(String(b), 'en', { numeric: true }));
  if (!semPac) {
    if (falhou0229) falhou['0065'] = { motivo: 'sem_0229', tentativas: 1 };
    else if (falhas0065.length) falhou['0065'] = { motivo: 'parcial', tentativas: 1, ids: ordenarIds(falhas0065) };
  }
  if (pede0024) {
    if (falhou0229 && pFat.length > 0) falhou['0024'] = { motivo: 'sem_0229', tentativas: 1 };
    else if (falhas0024.length) falhou['0024'] = { motivo: 'parcial', tentativas: 1, ids: ordenarIds(falhas0024) };
  }
  // Coerência entre relatórios (mesma régua das travas do Mac): 200 vazio
  // impossível diante de outro relatório da MESMA coleta = falha, não zero.
  const ok = (c) => S[c]?.status === 'fulfilled' && !falhou[c];
  const qtd0031 = ok('0031') ? (Number(uSvc.servicosRealizados) || 0) : null;
  const vis0002 = ok('0002') ? (Number(transformClientesAtendidos(S['0002'].value).visitas) || 0) : null;
  const atend0089 = ok('0089') ? (Number(uComandas.totalAtendimentos) || 0) : null;
  const fat0021 = ok('0021') ? pFat.reduce((a, p) => a + (Number(p.faturamento) || 0), 0) : null;
  const incoerente = (c) => { falhou[c] = { motivo: 'vazio_incoerente', tentativas: 1 }; vazio = vazio.filter(x => x !== c); };
  // 0089 sem atendimento só é falha com testemunha de SERVIÇO (0002 ou 0031):
  // dia só de venda de pacote tem 0281 > 0 e nenhuma comanda de serviço.
  if (atend0089 === 0 && ((vis0002 || 0) > 0 || (qtd0031 || 0) > 0)) incoerente('0089');
  if (qtd0031 === 0 && (atend0089 || 0) > 0) incoerente('0031');
  if (opts.withExtras0031 && ok('0031p') && vazio.includes('0031p') && (qtd0031 || 0) > 0) incoerente('0031p');
  if (ok('0123') && vazio.includes('0123') && ((fat0021 || 0) > 0 || (qtd0031 || 0) > 0)) incoerente('0123');

  // ── Campo que falhou vira null (nunca 0) ──
  const F = (c) => !!falhou[c];
  if (F('0281')) result.faturamento = null;
  if (F('0031')) result.servicosRealizados = null;
  if (F('0002')) result.clientesUnicos = null;
  if (F('0089')) { result.comandasServico = null; result.comandasProdutoServico = null; result.comandasProduto = null; }
  if (F('0012')) result.clientesNovos = null;
  if (F('0041')) { result.produtosVendidos = null; result.faturamentoProdutos = null; }
  if (F('0326')) { result.agendamentosPreferenciais = null; result.semPreferencia = null; }
  if (F('0062')) { result.pacotesVendidosTotal = null; result.valorPacotes = null; }
  if (F('0064')) result._comandasPacoteTotal = null;
  if (F('0007')) result.taxaRetorno = null;
  let visitasFonte = null;
  let visitasMudou = false;
  if (ATENDIDOS_SO_COMANDA_SERVICO) {
    // Atendidos = comanda com serviço (decisão do dono, 23/10/2026): 0089; se ele
    // falhou (ou veio zerado sem o 0031 confirmar), as comandas únicas do 0031
    // paginado quando vieram inteiras; senão sem dado. O 0002 nunca entra aqui.
    const ok0031p = !!opts.withExtras0031 && ok('0031p');
    const r = escolherAtendidos({
      ok0089: !F('0089'), atend0089, ok0031: ok('0031'), qtd0031,
      ok0031p, linhas0031p: ok0031p ? (S['0031p'].value?.result || []) : null,
      maxLinhas0031p: MAX_PAGINAS_0031P * AVEC_CONFIG.maxPageSize,
    });
    if (r.visitas == null && !DASH_SEM_DADO) {
      // Sem a tela nova (DASH_SEM_DADO desligada) os cartões antigos leem
      // `visitas || 0`: null viraria Atendidos 0, Ticket R$ 0 e Produtividade 0
      // sem aviso (o sync mensal de 60 s não pede o 0031 paginado). Fica a régua
      // de hoje: o 0002 se respondeu, senão null (como hoje).
      if (!F('0002')) { visitasFonte = '0002'; result.visitas = vis0002 ?? 0; }
      else result.visitas = null;
    } else {
      visitasMudou = r.visitas !== result.visitas;
      result.visitas = r.visitas;
      visitasFonte = r.fonte;
    }
  } else {
    // Atendidos: régua de hoje sem falha (0089 com atendimento, senão 0002).
    // 0089 falhou e 0002 respondeu: segue o 0002 como hoje (P2 neutra na tela);
    // a tela nova lê '0089' em _falhas e decide "Sem dado".
    if (!F('0089') && (atend0089 || 0) > 0) visitasFonte = '0089';
    else if (!F('0002')) { visitasFonte = '0002'; result.visitas = vis0002 ?? 0; }
    else result.visitas = null;
  }
  const falhas = Object.keys(falhou).sort();
  if (falhas.length || visitasMudou) {
    const fat = result.faturamento, vis = result.visitas, svc = result.servicosRealizados, pac = result._comandasPacoteTotal;
    result.geracaoDemanda = (result.semPreferencia == null || result.clientesNovos == null) ? null : result.semPreferencia + result.clientesNovos;
    if (fat == null || vis == null || pac == null) result.ticketMedio = null;
    else { const divisor = Math.max(vis - pac, 0); result.ticketMedio = divisor > 0 ? fat / divisor : 0; }
    result.ticketMedioBruto = (fat == null || vis == null) ? null : (vis > 0 ? fat / vis : 0);
    result.tracaoVendas = (svc == null || vis == null) ? null : (vis > 0 ? svc / vis : 0);
  }
  result._coleta = { v: VERSAO_COLETA, pedidos, falhou, vazio, herdado: {}, duracaoMs: agoraAvec() - t0Coleta };
  result._falhas = falhas;
  result._visitasFonte = visitasFonte;
  if (falhas.length) BRUTOS.set(result, { settled: S, r0065, withExtras0031: !!opts.withExtras0031 });
  return result;
}

// Resultados crus da coleta por unidade com falha (só memória: nunca vai para
// localStorage nem nuvem; some junto com o objeto). Base da nova tentativa só
// do que faltou.
const BRUTOS = new WeakMap();
export function transferirBrutos(de, para) {
  if (de && para && de !== para && BRUTOS.has(de)) BRUTOS.set(para, BRUTOS.get(de));
  return para;
}
export function temBrutos(unidade) { return !!unidade && BRUTOS.has(unidade); }

/**
 * Fetch isolado: serviços extras de UM profissional num range de datas.
 *
 * Usado pelo admin Dashboard escopo=pro pra calcular extras do prof no MESMO
 * período do mês anterior — sem precisar re-puxar todos os relatórios da unidade.
 * 1 chamada API (relatório 0024) por chamada desta função.
 *
 * @param {string} unitKey - chave AVEC_UNITS (ex: 'moema')
 * @param {string} avecProfId - ID do prof na Avec (vem de avecListCompleta no snapshot)
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {string} endDate - 'YYYY-MM-DD'
 * @returns {Promise<Array<{servico, qtd, faturamento}>>}
 */
// Bloqueios de agenda de UM prof num range (0159) — KPI ao vivo do dashboard
// individual (25/08/2026). Devolve { bloqueios, minutos }.
export async function syncProBloqueiosRange(unitKey, avecProfId, startDate, endDate) {
  const unit = AVEC_UNITS[unitKey];
  if (!unit || !unit.avecSalaoId) throw new Error(`Unidade "${unitKey}" sem avecSalaoId`);
  const inicio = formatAvecDate(parseISODate(startDate));
  const fim = formatAvecDate(parseISODate(endDate));
  const resp = await fetchReport('0159', {
    salao: unit.avecSalaoId, inicio, fim,
    profissional_id: avecProfId, page: 1, limit: 250,
  });
  const rows = resp?.result || [];
  let minutos = 0;
  for (const b of rows) minutos += Math.max(0, (Number(b.hora_fim) || 0) - (Number(b.hora_inicio) || 0));
  return { bloqueios: rows.length, minutos };
}

export async function syncProExtrasRange(unitKey, avecProfId, startDate, endDate) {
  const unit = AVEC_UNITS[unitKey];
  if (!unit?.avecSalaoId || !avecProfId) return [];
  const salao = unit.avecSalaoId;
  const inicio = formatAvecDate(parseISODate(startDate));
  const fim = formatAvecDate(parseISODate(endDate));
  try {
    const resp = await fetchReport('0024', { salao, inicio, fim, profissional_id: avecProfId });
    return transformServicosPorProfissional(resp);
  } catch {
    // null = FALHA (401/rede) — o consumidor cai no mapa mensal persistido.
    // [] seria "zero de verdade" e zerava a seção de extras em falha (10/09).
    return null;
  }
}

/**
 * Sincroniza TODOS os KPIs de uma unidade para um mês (wrapper sobre syncUnitRange).
 */
export async function syncUnitMonth(unitKey, year, month, opts = {}) {
  const { inicio, fim } = getMonthRange(year, month);
  // getMonthRange retorna DD/MM/YYYY — converto pra ISO YYYY-MM-DD
  const toISO = br => { const [d, m, y] = br.split('/'); return `${y}-${m}-${d}`; };
  return syncUnitRange(unitKey, toISO(inicio), toISO(fim), {
    ...opts,
    monthMeta: { _year: year, _month: month },
  });
}

/**
 * Sincroniza TODAS as unidades configuradas para um RANGE arbitrário.
 * Retorna { units: {key: data}, network: aggregatedTotals }
 *
 * IMPORTANTE: limita a 2 unidades em paralelo. Cada syncUnitRange faz 13
 * requests internos, então 5 unidades simultâneas = 65 requests paralelos
 * → API rate-limita → tudo retorna fallback zerado. Com 2 paralelas, são
 * 26 simultâneos (mais aceitável). Tradeoff: leva ~2-3x mais tempo.
 */
// 23/10/2026 (A1/A2): opts.unidades (só essas unidades: Meu Desempenho pede só a
// da pessoa) e opts.prazoMs (orçamento da coleta inteira: o que ainda estiver
// esperando quando vencer falha com motivo 'prazo' e aparece como "Sem dado").
export async function syncAllUnitsRange(startDate, endDate, opts = {}) {
  const CE = opts.coletaEstado ?? AVEC_COLETA_ESTADO;
  const t0 = agoraAvec();
  const unidadesPedidas = Array.isArray(opts.unidades) && opts.unidades.length ? opts.unidades : null;
  const units = getConfiguredUnits().filter(u => !unidadesPedidas || unidadesPedidas.includes(u.key));
  const CONCURRENCY = 2;
  const unitsData = {};
  const unitsArray = [];
  const optsUnidade = { ...opts };
  delete optsUnidade.unidades;
  delete optsUnidade.prazoMs;
  if (Number.isFinite(opts.prazoMs) && opts.deadline == null) optsUnidade.deadline = t0 + opts.prazoMs;

  // Processa em batches de CONCURRENCY
  for (let i = 0; i < units.length; i += CONCURRENCY) {
    const batch = units.slice(i, i + CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map(u => syncUnitRange(u.key, startDate, endDate, optsUnidade))
    );
    for (let j = 0; j < batch.length; j++) {
      const key = batch[j].key;
      if (results[j].status === 'fulfilled') {
        // Revisão 23/10/2026 (achado 1): unidade que o prazo cortou sem NENHUM
        // relatório que respondeu vira _error (sai da soma da rede; a tela antiga
        // cai no mensal como em qualquer _error; a nova mostra "Sem dado").
        if (CE && optsUnidade.deadline != null && cortadaPeloPrazo(results[j].value)) {
          unitsData[key] = { _error: 'prazo' };
          continue;
        }
        unitsData[key] = results[j].value;
        unitsArray.push({ unit: key, data: results[j].value });
      } else {
        unitsData[key] = { _error: results[j].reason?.message || 'erro desconhecido' };
      }
    }
  }
  const network = aggregateNetworkFromUnits(unitsArray);
  const out = {
    units: unitsData,
    network,
    _startDate: startDate,
    _endDate: endDate,
    _fetchedAt: new Date().toISOString(),
  };
  if (!CE) return out;
  out._variante = {
    withExtras0031: !!opts.withExtras0031,
    semPacotesPorProf: !!opts.semPacotesPorProf,
    unidades: unidadesPedidas ? [...unidadesPedidas].sort() : null,
    skipTaxaRetorno: !!opts.skipTaxaRetorno,
    skipExtrasPorProf: !!opts.skipExtrasPorProf,
  };
  out.network = { ...network, _faltando: faltandoNaRede(unitsData, units.map(u => u.key)) };
  out._duracaoMs = agoraAvec() - t0;
  return out;
}

// Diagnóstico (a tela calcula a rede em src/utils/kpisPeriodo.js): por campo,
// as unidades que falharam nele (ou vieram com _error / não vieram).
const CAMPOS_REDE = ['faturamento', 'servicosRealizados', 'visitas', 'clientesUnicos', 'clientesNovos',
  'produtosVendidos', 'faturamentoProdutos', 'agendamentosPreferenciais', 'semPreferencia', 'pacotesVendidosTotal'];
function faltandoNaRede(unitsData, esperadas) {
  const out = {};
  for (const campo of CAMPOS_REDE) {
    const f = esperadas.filter(k => { const u = unitsData[k]; return !u || u._error || u[campo] == null; });
    if (f.length) out[campo] = f;
  }
  return out;
}

// Rede recalculada depois de herança/nova tentativa (mesma soma de sempre +
// o diagnóstico _faltando).
export function recalcularRede(rangeData) {
  if (!rangeData?.units) return rangeData;
  const arr = Object.entries(rangeData.units).filter(([, u]) => u && !u._error).map(([unit, data]) => ({ unit, data }));
  return { ...rangeData, network: { ...aggregateNetworkFromUnits(arr), _faltando: faltandoNaRede(rangeData.units, Object.keys(rangeData.units)) } };
}

/**
 * Nova tentativa SÓ do que faltou num retorno do syncAllUnitsRange (botão
 * "Tentar de novo"). Unidade com resultado cru guardado refaz só os relatórios
 * (e grupos) que falharam; sem o cru (ou com _error) refaz a unidade inteira.
 * Campo que estava herdado continua herdado. Devolve objeto NOVO.
 * Com AVEC_COLETA_ESTADO desligado devolve a entrada sem tocar.
 */
export async function retentarFalhasRange(rangeData, opts = {}) {
  const CE = opts.coletaEstado ?? AVEC_COLETA_ESTADO;
  if (!CE || !rangeData?.units) return rangeData;
  const variante = rangeData._variante || {};
  const t0 = agoraAvec();
  const base = {
    skipTaxaRetorno: variante.skipTaxaRetorno ?? true,
    skipExtrasPorProf: variante.skipExtrasPorProf ?? true,
    withExtras0031: !!variante.withExtras0031,
    semPacotesPorProf: !!variante.semPacotesPorProf,
    prioridade: opts.prioridade ?? 0,
    coletaEstado: true,
  };
  if (Number.isFinite(opts.prazoMs)) base.deadline = t0 + opts.prazoMs;
  const units = { ...rangeData.units };
  const alvo = Object.entries(units).filter(([, u]) => !u || u._error || (Array.isArray(u._falhas) && u._falhas.length > 0)).map(([k]) => k);
  const CONCURRENCY = 2;
  for (let i = 0; i < alvo.length; i += CONCURRENCY) {
    const lote = alvo.slice(i, i + CONCURRENCY);
    const res = await Promise.allSettled(lote.map(async (k) => {
      const antes = units[k];
      const brutos = antes && !antes._error ? BRUTOS.get(antes) : null;
      const o = { ...base };
      if (brutos) {
        o.reaproveitar = brutos;
        o.somente = expandirGruposRetentativa(antes._falhas, { withExtras0031: !!brutos.withExtras0031 });
      }
      let nova = await syncUnitRange(k, rangeData._startDate, rangeData._endDate, o);
      // o prazo cortou a nova tentativa inteira: fica o que já havia (achado 1)
      if (cortadaPeloPrazo(nova)) return antes && !antes._error ? antes : { _error: 'prazo' };
      const herdados = Object.keys(antes?._coleta?.herdado || {});
      if (herdados.length) {
        const protegida = protegerCamposComFalha(antes, nova, { aceitarHerdado: true, apenas: herdados });
        nova = transferirBrutos(nova, protegida);
      }
      return nova;
    }));
    lote.forEach((k, j) => {
      if (res[j].status === 'fulfilled') units[k] = res[j].value;
      else if (!units[k] || units[k]._error) units[k] = { _error: res[j].reason?.message || 'erro desconhecido' };
    });
  }
  const arr = Object.entries(units).filter(([, u]) => u && !u._error).map(([unit, data]) => ({ unit, data }));
  const network = aggregateNetworkFromUnits(arr);
  return {
    ...rangeData,
    units,
    network: { ...network, _faltando: faltandoNaRede(units, Object.keys(units)) },
    _retentadoAt: new Date().toISOString(),
    _duracaoRetentativaMs: agoraAvec() - t0,
  };
}

/**
 * Sincroniza TODAS as unidades configuradas para um mês.
 * Limita a 2 unidades em paralelo (mesmo motivo do syncAllUnitsRange:
 * 5 paralelas × 13 reports = 65 requisições simultâneas → API rate-limita
 * → sync falha. Com 2 paralelas = 26 simultâneas, dentro do aceitável).
 */
export async function syncAllUnitsMonth(year, month, opts = {}) {
  const t0 = agoraAvec();
  const units = getConfiguredUnits();
  const CONCURRENCY = 2;
  const unitsData = {};
  const unitsArray = [];

  for (let i = 0; i < units.length; i += CONCURRENCY) {
    const batch = units.slice(i, i + CONCURRENCY);
    const results = await Promise.allSettled(
      batch.map(u => syncUnitMonth(u.key, year, month, opts))
    );
    for (let j = 0; j < batch.length; j++) {
      const key = batch[j].key;
      if (results[j].status === 'fulfilled') {
        unitsData[key] = results[j].value;
        unitsArray.push({ unit: key, data: results[j].value });
      } else {
        unitsData[key] = { _error: results[j].reason?.message || 'erro desconhecido' };
      }
    }
  }
  const network = aggregateNetworkFromUnits(unitsArray);
  const out = {
    units: unitsData,
    network,
    _year: year,
    _month: month,
    _fetchedAt: new Date().toISOString(),
  };
  // Duração da rodada (medida da cadência de 60 s): só com o estado da coleta
  if (opts.coletaEstado ?? AVEC_COLETA_ESTADO) out._duracaoMs = agoraAvec() - t0;
  return out;
}

/**
 * Sincroniza os últimos 12 meses para todas as unidades.
 * @param {function} onProgress - callback (done, total, current) para UI
 */
export async function syncLast12Months(onProgress) {
  const now = new Date();
  const results = {};
  const total = 12;

  for (let i = 0; i < total; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;

    const data = await syncAllUnitsMonth(year, month);
    results[key] = data;
    if (onProgress) onProgress(i + 1, total, key);
  }
  return results;
}

// ─── Consolidação (fechar mês) ─────────────────────────────────────────────

/**
 * Salva snapshot permanente de um mês (chamado quando o mês fecha).
 * Depois disso, o mês não é mais atualizado automaticamente.
 */
// Considera snapshot "vazio" se nenhuma unidade tem faturamento
function isSnapshotDataEmpty(unitsData) {
  for (const u of Object.values(unitsData || {})) {
    if ((u.faturamento || 0) > 0 || (u.visitas || 0) > 0) return false;
  }
  return true;
}

export function consolidateMonth(year, month, cacheData, { mesmoComFalha = false } = {}) {
  const key = `${year}-${String(month).padStart(2, '0')}`;
  const monthData = cacheData[key];
  if (!monthData) {
    console.warn(`Sem dados no cache para consolidar ${key}`);
    return;
  }
  // NÃO consolidar se os dados estão vazios (evita snapshot inútil)
  if (isSnapshotDataEmpty(monthData.units)) {
    console.warn(`Não consolidando ${key}: dados vazios`);
    return;
  }
  // 23/10/2026: unidade com relatório que falhou não vira snapshot permanente
  // (no cofre o snapshot sempre vence e nunca se cura, caso 2026-08). O mês
  // fica no cache e o syncLast12IfNeeded o refaz (no máximo 1 vez a cada 12 h,
  // até 3 vezes por aparelho: src/utils/avecRessync.js). Falha só de alguns
  // pedidos do leque por pessoa (0024/0065 'parcial') não segura o snapshot (a
  // herança guardou a lista de quem tinha). `mesmoComFalha`: o aparelho já
  // desistiu das novas tentativas; vira snapshot com o que veio (as falhas vão
  // marcadas em _falhas/_coleta da unidade).
  if (AVEC_COLETA_ESTADO && !mesmoComFalha && mesTemFalhas(monthData, false, { ignorarLeque: true })) {
    console.warn(`Não consolidando ${key}: há relatório que falhou em alguma unidade`);
    return;
  }
  for (const [unitKey, unitData] of Object.entries(monthData.units || {})) {
    setSnapshot(year, month, unitKey, unitData);
  }
  return key;
}

// Verifica se um snapshot/cache existe mas está vazio (precisa re-sync)
export function isSnapshotEmpty(snapshotMonth) {
  if (!snapshotMonth) return true;
  return isSnapshotDataEmpty(snapshotMonth);
}

/**
 * Verifica se o mês atual "virou" (passou para o próximo) e consolida o anterior.
 * Chamar isso no startup ou periodicamente.
 */
export function consolidateClosedMonths(cacheData, { mesmoComFalha = null } = {}) {
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const snapshots = getSnapshots();
  const consolidated = [];
  // meses em que este aparelho já desistiu das novas tentativas (avecRessync.js)
  let desistiu = mesmoComFalha;
  if (!desistiu && AVEC_COLETA_ESTADO) {
    try { desistiu = mesesParaConsolidarComFalha(lerMarcasRessync(localStorage.getItem(CHAVE_RESSYNC))); } catch { desistiu = new Set(); }
  }

  for (const [key, data] of Object.entries(cacheData)) {
    if (key >= currentKey) continue; // só fecha meses já passados
    if (snapshots[key]) continue; // já foi consolidado
    const [y, m] = key.split('-').map(Number);
    const r = consolidateMonth(y, m, { [key]: data }, { mesmoComFalha: !!desistiu?.has?.(key) });
    // 23/10/2026: só conta o que virou snapshot de fato (antes contava sempre)
    if (!AVEC_COLETA_ESTADO || r) consolidated.push(key);
  }
  return consolidated;
}

// ─── Export default ────────────────────────────────────────────────────────
export default {
  syncUnitMonth,
  syncAllUnitsMonth,
  retentarFalhasRange,
  syncLast12Months,
  consolidateMonth,
  consolidateClosedMonths,
  getCache,
  setCache,
  getSnapshots,
};
